import {
  Button,
  EmptyState,
  FormField,
  Icon,
  ListToolbar,
  PageHeader,
  Section,
  Select,
  Skeleton,
  Text,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../../lib/errors';
import { useFacetTexts } from '../../../lib/facets';
import { useResetUrlState, useUrlState } from '../../../lib/useUrlState';
import { useLocations } from '../../apiary/api';
import { useSession } from '../../identity/session';
import { ExportButton } from '../../reports/components/ExportButton';
import { MyMarketDays } from '../../sales/components/MyMarketDays';
import { useMyMarketDays } from '../../sales/api';
import { useAssignees, useTasks, type TaskView } from '../api';
import { TaskFormDialog } from '../components/TaskFormDialog';
import { TaskList } from '../components/TaskList';
import styles from './TasksPages.module.css';

const VIEWS: readonly TaskView[] = ['today', 'upcoming', 'mine', 'all'];
const FILTER_KEYS = ['standort', 'zustaendig'] as const;

/** Task list with the views today, upcoming, mine and all (TSK-003). */
export function TasksPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [view, setView] = useUrlState<TaskView>('ansicht', 'today', VIEWS);
  const [location, setLocation] = useUrlState('standort', '');
  const [assigneeParam, setAssignee] = useUrlState('zustaendig', '');
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const facetTexts = useFacetTexts(resetFilters);
  const [creating, setCreating] = useState(false);
  const assignee = view === 'mine' ? '' : assigneeParam;
  const tasks = useTasks({
    view,
    location_id: location || undefined,
    assignee_id: assignee || undefined,
  });
  const today = useTasks({ view: 'today' });
  const locations = useLocations(false, can('locations.read'));
  const assignees = useAssignees();
  const marketDays = useMyMarketDays(can('sales.read'));
  const rows = tasks.data ?? [];
  const locationName = locations.data?.find((l) => l.id === location)?.name;
  const assigneeName = assignees.data?.find((a) => a.id === assignee)?.name;
  const filtered = location !== '' || assignee !== '';
  const write = can('tasks.write');
  const create = () => setCreating(true);

  const chips = [
    ...(location
      ? [
          {
            key: 'location',
            label: t('tasks.filters.chipLocation', { name: locationName ?? '…' }),
            onRemove: () => setLocation(''),
          },
        ]
      : []),
    ...(assignee
      ? [
          {
            key: 'assignee',
            label: t('tasks.filters.chipAssignee', { name: assigneeName ?? '…' }),
            onRemove: () => setAssignee(''),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('tasks.title')}
        actions={
          <>
            {write && (
              <Button iconStart={<Icon name="plus" size="sm" />} onClick={create}>
                {t('tasks.new')}
              </Button>
            )}
            <ExportButton list="tasks" />
          </>
        }
      />

      <ListToolbar
        scope={{
          label: t('tasks.views.label'),
          value: view,
          onChange: (v) => setView(v as TaskView),
          options: VIEWS.map((v) => ({
            value: v,
            label:
              v === 'today' && (today.data?.length ?? 0) > 0
                ? `${t(`tasks.views.${v}`)} (${today.data?.length})`
                : t(`tasks.views.${v}`),
          })),
        }}
        facets={{
          ...facetTexts,
          items: [
            ...(can('locations.read')
              ? [
                  {
                    type: 'select' as const,
                    key: 'location',
                    label: t('tasks.filters.location'),
                    value: location,
                    options: (locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                    onChange: setLocation,
                  },
                ]
              : []),
            ...(view !== 'mine'
              ? [
                  {
                    type: 'select' as const,
                    key: 'assignee',
                    label: t('tasks.filters.assignee'),
                    value: assignee,
                    options: (assignees.data ?? []).map((a) => ({ value: a.id, label: a.name })),
                    onChange: setAssignee,
                  },
                ]
              : []),
          ],
        }}
        {...(can('locations.read') || view !== 'mine'
          ? {
              filters: {
                label: t('common.filter'),
                activeCount: chips.length,
                content: (
                  <>
                    {can('locations.read') && (
                      <FormField label={t('tasks.filters.location')}>
                        {(p) => (
                          <Select
                            {...p}
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            options={[
                              { value: '', label: t('tasks.filters.allLocations') },
                              ...(locations.data ?? []).map((l) => ({
                                value: l.id,
                                label: l.name,
                              })),
                            ]}
                          />
                        )}
                      </FormField>
                    )}
                    {view !== 'mine' && (
                      <FormField label={t('tasks.filters.assignee')}>
                        {(p) => (
                          <Select
                            {...p}
                            value={assignee}
                            onChange={(e) => setAssignee(e.target.value)}
                            options={[
                              { value: '', label: t('tasks.filters.everyone') },
                              ...(assignees.data ?? []).map((a) => ({
                                value: a.id,
                                label: a.name,
                              })),
                            ]}
                          />
                        )}
                      </FormField>
                    )}
                  </>
                ),
              },
            }
          : {})}
        chips={{
          chips,
          removeLabel: (label) => t('common.removeFilter', { label }),
          resetLabel: t('common.resetAll'),
          onReset: resetFilters,
        }}
      />

      {!filtered && (marketDays.data?.length ?? 0) > 0 && (
        <Section title={t('sales.markets.mine.title')} variant="flush">
          <MyMarketDays days={marketDays.data ?? []} />
        </Section>
      )}

      <Section>
        {tasks.error ? (
          <Text tone="danger" role="alert" className={styles.empty}>
            {errorMessage(tasks.error)}
          </Text>
        ) : tasks.isPending ? (
          <div role="status" aria-label={t('common.loading')} className={styles.empty}>
            <Skeleton lines={5} />
          </div>
        ) : rows.length === 0 ? (
          filtered ? (
            <EmptyState
              title={t('common.noResults')}
              actions={
                <Button variant="secondary" onClick={resetFilters}>
                  {t('common.resetFilters')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon="list-checks"
              title={t(`tasks.empty.${view}.title`)}
              actions={write && <Button onClick={create}>{t('tasks.new')}</Button>}
            />
          )
        ) : (
          <TaskList tasks={rows} />
        )}
      </Section>

      {creating && <TaskFormDialog open onClose={() => setCreating(false)} />}
    </div>
  );
}
